/**
 * Vendor data completeness — TRBUD-466 Part 1.
 *
 * Matt and Brett fill in the vendor records a few at a time. This page says, for
 * each field, how many vendors in use have it, worst first, and each row opens
 * the Vendors list narrowed to the vendors MISSING that field. The rule for
 * "filled" lives once, server-side, in `apps/api/src/read/vendor-completeness.ts`
 * (`fieldState`), and the page's counts and the list's rows are both built on it.
 *
 * ## NO IMPORTS, on purpose
 *
 * `requests.ts` reads `vendorCompletenessFields` for the vendor list's `missing`
 * filter. Anything here importing from `requests.ts` (`queryBoolean`, say) would
 * close a cycle between the two modules, so the zod schemas stay in
 * `requests.ts` and this file holds only the keys, the labels, the wire shapes
 * and two pieces of arithmetic. Browser-safe.
 */

/**
 * The fields the page counts, in the order they read when two are tied.
 *
 * Left off, deliberately: the three type flags (one is always set, so they
 * would always read 100%), and rating, terms, service area and pricing program,
 * which have no column (they are the demo overlay's) and so cannot be filled in.
 */
export const vendorCompletenessFields = [
  "phone",
  "email",
  "website",
  "streetAddress",
  "cityState",
  "mapPosition",
  "primaryContact",
  "hasAccount",
  "accountReference",
  "chain",
  "mobileService",
  "logo",
] as const;
export type VendorCompletenessField = (typeof vendorCompletenessFields)[number];

/** What each field is called on the page and in the Vendors list's banner. */
export const vendorCompletenessLabels: Readonly<Record<VendorCompletenessField, string>> = {
  phone: "Phone",
  email: "Email",
  website: "Website",
  streetAddress: "Street address",
  cityState: "City and state",
  mapPosition: "Placed on the map",
  primaryContact: "Primary contact",
  hasAccount: "Has an account with us",
  accountReference: "Account reference",
  chain: "Part of a chain",
  mobileService: "Comes to the truck",
  logo: "Logo",
};

/** A key read off a web address, which is untrusted text until checked. */
export function isVendorCompletenessField(value: unknown): value is VendorCompletenessField {
  return typeof value === "string" && (vendorCompletenessFields as readonly string[]).includes(value);
}

/**
 * One vendor against one field. `not_applicable` is a vendor the field does not
 * ask anything of: a street address for a vendor that comes to the truck, or an
 * account reference where there is no account. It counts in neither total.
 */
export type VendorFieldState = "filled" | "missing" | "not_applicable";

export interface VendorCompletenessFieldSummary {
  field: VendorCompletenessField;
  label: string;
  /** Vendors in scope the field applies to: `filled + missing`. */
  applies: number;
  filled: number;
  missing: number;
}

/** `GET /vendors/completeness`. */
export interface VendorCompletenessResponse {
  /** Whether inactive and blacklisted vendors are counted too. */
  includeRetired: boolean;
  /** Vendors counted: active and conditional, plus the retired two when asked. */
  vendorsInScope: number;
  /** Worst first (`worstFirst`). */
  fields: VendorCompletenessFieldSummary[];
}

/**
 * The share filled, as a whole percent, or `null` when the field applies to no
 * vendor yet.
 *
 * Rounded DOWN, so 100% is shown only when every vendor has the field: 299 of
 * 300 is 99%, never a rounded-up 100% that would read as finished.
 */
export function completenessPercent(filled: number, applies: number): number | null {
  if (applies <= 0) return null;
  return Math.floor((filled * 100) / applies);
}

/** QuoteBuddy's bands: 80% and up, 40 to 79%, under 40%. */
export type CompletenessBand = "good" | "partial" | "poor";

export function completenessBand(filled: number, applies: number): CompletenessBand | null {
  const percent = completenessPercent(filled, applies);
  if (percent === null) return null;
  if (percent >= 80) return "good";
  if (percent >= 40) return "partial";
  return "poor";
}

/**
 * Worst first: the lowest share filled at the top, a field that applies to no
 * vendor yet at the bottom, ties in `vendorCompletenessFields` order. Returns a
 * new array.
 */
export function worstFirst<T extends { field: VendorCompletenessField; filled: number; applies: number }>(
  rows: readonly T[],
): T[] {
  const order = (field: VendorCompletenessField) => vendorCompletenessFields.indexOf(field);
  return [...rows].sort((a, b) => {
    const aNone = a.applies <= 0;
    const bNone = b.applies <= 0;
    if (aNone !== bNone) return aNone ? 1 : -1;
    if (!aNone) {
      // Cross-multiplied rather than divided, so equal shares tie exactly.
      const byShare = a.filled * b.applies - b.filled * a.applies;
      if (byShare !== 0) return byShare;
    }
    return order(a.field) - order(b.field);
  });
}
